import {useState} from 'react';

import {promiseStatuses} from './hooks.utils';

/**
 * This hooks takes an async callback function and returns an object that has the status
 * of calling the callback function. If the function has not been called, the status is 'staged'. Otherwise,
 * it is one of the shared `promiseStatuses`.
 *
 * @param {Function} cb Callback function that you wish to augment
 * @returns {ReturnState} state
 */
export default function useAsyncFunctionStatus(cb) {
  const [state, setState] = useState({
    status: 'staged',
    result: null
  });

  const wrappedCb = async (...args) => {
    setState((currState) => ({
      ...currState,
      status: promiseStatuses.pending
    }));
    try {
      const result = await cb(...args);
      setState({
        status: promiseStatuses.success,
        result
      });
    } catch (err) {
      setState({
        status: promiseStatuses.error,
        result: err
      });
    }
  };

  return {
    status: state.status,
    result: state.result,
    callback: wrappedCb
  };
}

/**
 * @typedef {object} ReturnState
 * @property {string} status Function status.
 * @property {*} result If the function was successful, this is the value the function. If
 *   there was an error, this is the error.
 * @property {Function} callback The augmented callback function. Calling this will update the status.
 */
